/**
 * CF7 Google Places Autocomplete
 */

declare const google: {
    maps: {
        importLibrary: (name: string) => Promise<any>;
        places: any;
    };
};

declare global {
    interface Window {
        cf7gpaConfig: CF7GPAConfig;
        cf7gpaPlaces: CF7GPAPlaces | null;
        cf7gpaInit: () => Promise<void>;
        google: typeof google;
    }
}

interface CF7GPAConfig {
    countryCodes: string[];
    addressFields: string[];
    language: string;
    debug: boolean;
    placeFields: string[];
}

interface CF7GPAPlaces {
    Place: any;
    AutocompleteSessionToken: any;
    AutocompleteSuggestion: any;
}


const instances = new Map<string, CF7GPAAutocomplete>();

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const MIN_CHARS = 3;
const DEBOUNCE_DELAY = 300;


export async function cf7gpaInit(): Promise<void> {

    if (typeof google === 'undefined' || !google.maps) {
        console.error('CF7GPA: Google Maps API not loaded');
        return;
    }
    
    try {
        const placesLib = await google.maps.importLibrary('places') as any;
        
        const { Place, AutocompleteSessionToken, AutocompleteSuggestion } = placesLib;
        
        window.cf7gpaPlaces = { Place, AutocompleteSessionToken, AutocompleteSuggestion };

        initAutocompleteFields();

        document.addEventListener('wpcf7mailsent', () => {
            setTimeout(initAutocompleteFields, 100);
        });
        
    } catch (error) {
        console.error('CF7GPA: Failed to load Places library', error);
    }
}

window.cf7gpaInit = cf7gpaInit;

function initAutocompleteFields(): void {
    const containers = document.querySelectorAll<HTMLElement>('[data-cf7gpa-container]');
    
    
    containers.forEach(container => {
        const fieldName = container.getAttribute('data-cf7gpa-container');
        if (!fieldName) return;
        
        if (instances.has(fieldName)) {
            return;
        }
        
        const input = container.querySelector<HTMLInputElement>('[data-cf7gpa-field="main"]');
        if (!input) {
            return;
        }
        
        const instance = new CF7GPAAutocomplete(container, input, fieldName);
        instances.set(fieldName, instance);
    });
}

class CF7GPAAutocomplete {
    private container: HTMLElement;
    private input: HTMLInputElement;
    private fieldName: string;
    private suggestionsEl: HTMLElement | null;
    private infoWrapper: HTMLElement | null;
    
    private sessionToken: any = null;
    private placeSelected = false;
    private suggestions: any[] = [];
    private selectedIndex = -1;
    private requestId = 0;
    
    constructor(container: HTMLElement, input: HTMLInputElement, fieldName: string) {
        this.container = container;
        this.input = input;
        this.fieldName = fieldName;
        this.suggestionsEl = container.querySelector('.cf7gpa-suggestions');
        this.infoWrapper = container.querySelector('.cf7gpa-info-wrapper');
        
        this.init();
    }
    
    private init(): void {
        this.refreshSessionToken();
        
        this.input.addEventListener('input', this.onInput.bind(this));
        this.input.addEventListener('keydown', this.onKeyDown.bind(this));
        this.input.addEventListener('focusout', this.onFocusOut.bind(this));
        
        document.addEventListener('click', (e) => {
            if (!this.container.contains(e.target as Node)) {
                this.hideSuggestions();
            }
        });
    }
    
    /**
     * Create a new session token
     */
    private refreshSessionToken(): void {
        if (window.cf7gpaPlaces?.AutocompleteSessionToken) {
            this.sessionToken = new window.cf7gpaPlaces.AutocompleteSessionToken();
        }
    }
    
    private onInput(event: Event): void {
        const target = event.target as HTMLInputElement;
        const value = target.value.trim();
        
        // Reset place selected flag
        this.placeSelected = false;
        this.input.removeAttribute('data-place');
        
        // Clear secondary fields
        this.clearAddressFields();
        
        // Clear debounce timer
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        
        // Don't search if too short
        if (value.length < MIN_CHARS) {
            this.hideSuggestions();
            return;
        }
        
        // Debounce the search
        debounceTimer = setTimeout(() => {
            this.fetchSuggestions(value);
        }, DEBOUNCE_DELAY);
    }
    
    private async fetchSuggestions(query: string): Promise<void> {
        if (!window.cf7gpaPlaces) {
            console.error('CF7GPA: Places library not loaded');
            return;
        }
        
        if (!window.cf7gpaPlaces.AutocompleteSuggestion) {
            console.error('CF7GPA: AutocompleteSuggestion not available');
            return;
        }
        
        const requestId = ++this.requestId;
        
        try {
            // Build the request with session token
            const request: any = {
                input: query,
                sessionToken: this.sessionToken,
                language: window.cf7gpaConfig?.language || 'it',
            };
            
            // Add country restriction if configured
            if (window.cf7gpaConfig?.countryCodes?.length > 0) {
                request.includedRegionCodes = window.cf7gpaConfig.countryCodes;
            }
            
            // Fetch suggestions
            const response = await window.cf7gpaPlaces.AutocompleteSuggestion
                .fetchAutocompleteSuggestions(request);
            
            const { suggestions } = response;
            
            // Ignore if a newer request was made
            if (requestId !== this.requestId) return;
            
            this.suggestions = suggestions;
            this.renderSuggestions();
            
        } catch (error) {
            console.error('CF7GPA: Error fetching suggestions', error);
            this.showError('Errore nel caricamento dei suggerimenti');
        }
    }
    
    private renderSuggestions(): void {
        if (!this.suggestionsEl) return;
        
        if (!this.suggestions || this.suggestions.length === 0) {
            this.suggestionsEl.innerHTML = `
                <div class="cf7gpa-suggestion cf7gpa-no-results">
                    Nessun risultato trovato
                </div>
            `;
            this.showSuggestions();
            return;
        }
        
        this.selectedIndex = -1;
        
        const html = this.suggestions.map((suggestion, index) => {
            const prediction = suggestion.placePrediction;
            const text = prediction?.text?.toString() ?? '';
            
            return `
                <div class="cf7gpa-suggestion" 
                     data-index="${index}"
                     role="option"
                     aria-selected="false">
                    <span class="cf7gpa-suggestion-text">${this.highlightMatch(text)}</span>
                </div>
            `;
        }).join('');
        
        this.suggestionsEl.innerHTML = html;
        
        // Add click handlers
        this.suggestionsEl.querySelectorAll<HTMLElement>('.cf7gpa-suggestion[data-index]').forEach(el => {
            el.addEventListener('click', this.onSuggestionClick.bind(this));
        });
        
        this.showSuggestions();
    }

    private highlightMatch(text: string): string {
        const query = this.input.value.trim();
        if (!query) return text;
        
        const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
        return text.replace(regex, '<strong>$1</strong>');
    }
    
    /**
     * Escape special regex characters
     */
    private escapeRegex(string: string): string {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    private onSuggestionClick(event: Event): void {
        const el = event.currentTarget as HTMLElement;
        const index = parseInt(el.getAttribute('data-index') ?? '', 10);
        
        if (!isNaN(index) && this.suggestions[index]) {
            this.selectSuggestion(index);
        }
    }
    
    private async selectSuggestion(index: number): Promise<void> {
        const suggestion = this.suggestions[index];
        if (!suggestion?.placePrediction) return;
        
        const prediction = suggestion.placePrediction;
        
        // Update main input with the selected text
        this.input.value = prediction.text?.toString() ?? '';
        this.input.setAttribute('data-place', 'selected');
        this.placeSelected = true;
        
        this.hideSuggestions();
        
        try {
            // Convert to Place object and fetch details
            // The session token is automatically included
            const place = prediction.toPlace();
            
            // Fetch only the fields we need (Fields Masks for cost optimization)
            await place.fetchFields({
                fields: ['formattedAddress', 'addressComponents', 'displayName']
            });
            
            // Parse and fill address fields
            this.fillAddressFields(place);
            
            // Refresh session token for next session
            this.refreshSessionToken();
            
        } catch (error) {
            console.error('CF7GPA: Error fetching place details', error);
        }
    }
    
    private fillAddressFields(place: any): void {
        if (!place.addressComponents) return;
        
        const components = place.addressComponents;
        let streetNumber = '';
        let route = '';
        let locality = '';
        let adminArea1 = '';
        let adminArea2 = '';
        let adminArea3 = '';
        let postalCode = '';
        let country = '';
        
        components.forEach((component: any) => {
            const types = component.types;
            
            if (types.includes('street_number')) {
                streetNumber = component.longText ?? '';
            }
            if (types.includes('route')) {
                route = component.longText ?? '';
            }
            if (types.includes('locality')) {
                locality = component.longText ?? '';
            }
            if (types.includes('administrative_area_level_1')) {
                adminArea1 = component.shortText ?? '';
            }
            if (types.includes('administrative_area_level_2')) {
                adminArea2 = component.shortText ?? '';
            }
            if (types.includes('administrative_area_level_3')) {
                adminArea3 = component.shortText ?? '';
            }
            if (types.includes('postal_code')) {
                postalCode = component.longText ?? '';
            }
            if (types.includes('country')) {
                country = component.longText ?? '';
            }
        });
        
        // Build full street address
        const streetAddress = streetNumber 
            ? `${route}, ${streetNumber}` 
            : route;
        
        this.setFieldValue('address', streetAddress);
        this.setFieldValue('locality', locality || adminArea3);
        this.setFieldValue('state', adminArea2 || adminArea1);
        this.setFieldValue('region', adminArea1);
        this.setFieldValue('postcode', postalCode);
        this.setFieldValue('country', country);
        
        if (this.infoWrapper) {
            this.infoWrapper.classList.add('active');
        }
    }
    
    private setFieldValue(fieldType: string, value: string): void {
        const field = this.container.querySelector<HTMLInputElement>(`[data-cf7gpa-field="${fieldType}"]`);
        if (field) {
            field.value = value ?? '';
        }
    }
    
    private clearAddressFields(): void {
        const fields = this.container.querySelectorAll<HTMLInputElement>('[data-cf7gpa-field]:not([data-cf7gpa-field="main"])');
        fields.forEach(field => {
            field.value = '';
        });
        
        if (this.infoWrapper) {
            this.infoWrapper.classList.remove('active');
        }
    }
    
    private onKeyDown(event: KeyboardEvent): void {
        if (!this.suggestionsEl) return;
        
        const suggestionsVisible = this.suggestionsEl.classList.contains('active');
        
        if (!suggestionsVisible) return;
        
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.navigateSuggestions(1);
                break;
                
            case 'ArrowUp':
                event.preventDefault();
                this.navigateSuggestions(-1);
                break;
                
            case 'Enter':
                event.preventDefault();
                if (this.selectedIndex >= 0) {
                    this.selectSuggestion(this.selectedIndex);
                }
                break;
                
            case 'Escape':
                this.hideSuggestions();
                break;
        }
    }
    

    private navigateSuggestions(direction: number): void {
        if (!this.suggestionsEl) return;
        
        const items = this.suggestionsEl.querySelectorAll<HTMLElement>('.cf7gpa-suggestion[data-index]');
        if (items.length === 0) return;
        
        // Update selected index
        this.selectedIndex += direction;
        
        // Wrap around
        if (this.selectedIndex < 0) {
            this.selectedIndex = items.length - 1;
        } else if (this.selectedIndex >= items.length) {
            this.selectedIndex = 0;
        }
        
        // Update visual state
        items.forEach((item, index) => {
            item.classList.toggle('selected', index === this.selectedIndex);
            item.setAttribute('aria-selected', String(index === this.selectedIndex));
        });
        
        // Scroll into view
        items[this.selectedIndex]?.scrollIntoView({ block: 'nearest' });
    }

    private onFocusOut(): void {
        // Delay to allow click on suggestion to register
        setTimeout(() => {
            // If no place was selected, clear the input
            if (!this.placeSelected && !this.input.hasAttribute('data-place')) {
                this.input.value = '';
                this.clearAddressFields();
            }
            this.hideSuggestions();
        }, 200);
    }
    
    private showSuggestions(): void {
        this.suggestionsEl?.classList.add('active');
    }

    private hideSuggestions(): void {
        this.suggestionsEl?.classList.remove('active');
        this.selectedIndex = -1;
    }

    private showError(message: string): void {
        if (!this.suggestionsEl) return;
        
        this.suggestionsEl.innerHTML = `
            <div class="cf7gpa-suggestion cf7gpa-error">
                ${message}
            </div>
        `;
        this.showSuggestions();
    }
}

// Auto-init if Google Maps is already loaded
if (typeof window !== 'undefined' && window.google?.maps) {
    cf7gpaInit();
}
